import { Injectable } from '@angular/core';
import { ProjectUtils } from '@project/components/project/util/project-utils';
import { ApplicationService } from '@shared/application/application.service';
import { StringUtils } from '@shared/util/string-utils';
import { CaeData, CaeProject, CaeRail } from '@project/cae/cae-data.service';
import { ProjectGroupVO } from '@project/model/vo/project-group.vo';
import { ProjectGroupUtils } from '@project/components/project/util/project-group-utils';
import { ProjectModelService } from '@project/service/project-model.service';
import { ProjectService } from '@project/components/import-export/service/project.service';
import { ProjectHeadVO } from '@project/model/vo/project.vo';
import { ProjectModel } from '@project/model/project.model';
import { PropertyUtils } from '@article/util/property-utils';
import { ArticleUsageInfoPropertyDef } from '@article/constants/article-usage-info-property-def';
import { ArticleConstants } from '@article/constants/article-constants';
import { Log } from '@shared/debug/log';
import { ArticleUsageVO, ArticleVO } from '@article/model/vo/article.vo';
import { ProjectArticleService } from '@project/service/project-article.service';
import { ArticleManager } from '@article/manager/article.manager';
import { CaeImportHelperService } from './cae-import-helper.service';
import { CaeProjectImportUtils } from '@project/cae/util/cae-project-import-utils';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { AttachArticleUsageService } from '@project/service/attach-article-usage.service';
import { lastValueFrom } from 'rxjs';
import { ImportedCaeProjectDTO } from '@project/model/vo/imported-cae-project.dto';
import { InitManager } from '@shared/manager/init.manager';
import { ProjectOptionUtils } from '@project/components/project/util/project-option.utils';
import { ProjectUserModel } from '@project/model/project-user.model';
import { CarrierBlockManager } from '@project/components/article/manager/carrier-block.manager';
import { OBJRepresentationDataVO, RepresentationDataVO } from '@shared/vo/representation.vo';
import { RepresentationService } from '@project/service/representation.service';
import { ProjectNavigationPresenter } from '@project-navigation/project-navigation.presenter';
import { SaveCaeProjectService } from '@project/components/project/project-save/save-cae-project.service';
import { CaeRailsData } from './cae-rails-data';

@Injectable({
  providedIn: 'root',
})
export class CaeImportProjectService {

  constructor(
    private applicationService: ApplicationService,
    private projectModelService: ProjectModelService,
    private projectService: ProjectService,
    private projectModel: ProjectModel,
    private articleManager: ArticleManager,
    private projectArticleService: ProjectArticleService,
    private caeImportHelperService: CaeImportHelperService,
    private attachArticleUsageService: AttachArticleUsageService,
    private projectUserModel: ProjectUserModel,
    private carrierBlockManager: CarrierBlockManager,
    private representationService: RepresentationService,
    private projectNavigationPresenter: ProjectNavigationPresenter,
    private saveCaeProjectService: SaveCaeProjectService,
  ) {
  }

  public async createProject(caeData: CaeData, caeRailsData: CaeRailsData): Promise<void> {
    const projectVO = ProjectUtils.createNewProjectVO(this.applicationService.applicationVersion);
    projectVO.guid = StringUtils.createUuid();
    projectVO.caeType = caeData.caeType;
    projectVO.exported = caeData.exported;
    projectVO.name = this.createProjectName(caeRailsData.currentRails);
    projectVO.projectGroup = new ProjectGroupVO();
    projectVO.projectGroup.id = -1;
    projectVO.projectGroup.groupType = ProjectGroupUtils.PROJECT;
    projectVO.projectGroup.guid = caeData.project.id;
    projectVO.projectGroup.groupName = caeData.project.name;
    projectVO.projectGroup.projects = [ projectVO ];
    await this.projectModelService.setCurrentProject(projectVO);
    await this.addMountingRail(caeRailsData.currentRails[0]);
  }

  private createProjectName(caeRails: CaeRail[]): string {
    const projectNames = caeRails
      .map((caeRail) => this.getProjectNameForRail(caeRail))
      .filter((name) => name);
    const projectName = projectNames.join(' ');
    const truncatedProjectName = StringUtils.isEmpty(projectName)
      ? this.projectService.getDefaultProjectName()
      : StringUtils.truncate(projectName, ProjectHeadVO.MAX_NAME_LENGTH);
    return truncatedProjectName;
  }

  private getProjectNameForRail(caeRail: CaeRail): string | undefined {
    return StringUtils.isEmpty(caeRail.structure) ? undefined : caeRail.structure;
  }

  public storeMountingInfos(caeRail: CaeRail) {
    this.storeMountInfoToCurrentProject(caeRail);
    this.storeRailInfoToRailUsage(caeRail);
  }

  private storeMountInfoToCurrentProject(caeRail: CaeRail) {
    if (caeRail.mount) {
      const projectInfoProperties = this.projectModel.currentProject.infoProperties;
      projectInfoProperties.setString('caeMountId', caeRail.mount.id);
      projectInfoProperties.setString('caeMountStructure', caeRail.mount.structure);
      projectInfoProperties.setString('caeMountName', caeRail.mount.name);
      projectInfoProperties.setFloat('caeMountOffsetX', caeRail.mount.offset.x);
      projectInfoProperties.setFloat('caeMountOffsetY', caeRail.mount.offset.y);
      projectInfoProperties.setFloat('caeMountOffsetZ', caeRail.mount.offset.z);
      projectInfoProperties.setFloat('caeMountSizeWidth', caeRail.mount.size.width);
      projectInfoProperties.setFloat('caeMountSizeHeight', caeRail.mount.size.height);
      projectInfoProperties.setFloat('caeMountSizeDepth', caeRail.mount.size.depth);
    }
  }

  private storeRailInfoToRailUsage(rail: CaeRail) {
    const railArticleUsage = ProjectUtils.getFirstArticleUsage(this.projectModel.currentProject);
    if (railArticleUsage) {
      PropertyUtils.setIntPropertyValue(railArticleUsage.infoProperties, ArticleUsageInfoPropertyDef.CAE_RAIL_ROTATION, rail.rotation);
      PropertyUtils.setFloatPropertyValue(railArticleUsage.infoProperties, ArticleUsageInfoPropertyDef.CAE_RAIL_OFFSET_X, rail.offset.x);
      PropertyUtils.setFloatPropertyValue(railArticleUsage.infoProperties, ArticleUsageInfoPropertyDef.CAE_RAIL_OFFSET_Y, rail.offset.y);
      PropertyUtils.setFloatPropertyValue(railArticleUsage.infoProperties, ArticleUsageInfoPropertyDef.CAE_RAIL_OFFSET_Z, rail.offset.z);
    }
  }

  private async addMountingRail(rail: CaeRail): Promise<void> {
    const carrierRail = await lastValueFrom(this.articleManager.getArticleByArticleNo(ArticleConstants.FREE_MOUNTING_ARTICLE_NO));
    if (Log.assertExists(carrierRail)) {
      await this.continueAddMountingRail(carrierRail, rail);
    }
  }

  private async continueAddMountingRail(mountingRailArticle: ArticleVO, rail: CaeRail): Promise<void> {
    this.projectModel.newProjectMountingRail();
    const mountingRailUsage = this.projectArticleService.createNewArticleUsage(mountingRailArticle.identity, mountingRailArticle.version);

    this.caeImportHelperService.replaceArticleGuid(mountingRailUsage, rail.id);
    this.writeMountingRailPrefix(mountingRailUsage, rail.articleNo);

    this.projectModelService.currentProjectType = ProjectUtils.getProjectTypeByMountingRailArticle(mountingRailArticle);
    await this.attachArticleUsageService.attachArticleUsageToProject(mountingRailUsage);
  }

  private writeMountingRailPrefix(articleUsage: ArticleUsageVO, mountingRailNo: string): void {
    const articleNo: string = mountingRailNo;
    if (articleNo && CaeProjectImportUtils.hasWagoArticleNumberPrefix(articleNo)) {
      ArticleUsageUtils.setArticleUsageInfoPropertyStringValue(articleUsage, ArticleUsageInfoPropertyDef.ARTICLE_NUMBER_WITH_PREFIX, articleNo);
    }
  }

  public createImportedCaeProject(caeData: CaeData): ImportedCaeProjectDTO {
    this.projectModel.currentInfoProperties.setString('projectOptionDataVersion', InitManager.PROJECT_OPTION_DATA_VERSION);
    ProjectOptionUtils.setDefaultProjectOptionValues(this.projectModel.currentProject, this.projectUserModel.projectOptionDefs);
    this.carrierBlockManager.updateCarrierBlocksInProject(this.projectModel.currentProject);
    this.representationService.update();
    const representationData: RepresentationDataVO = ProjectUtils.findFirstView3DRepresentationData(this.projectModel.currentProject);
    this.projectModel.currentProject.caeType = caeData.caeType;
    this.projectModel.currentProject.exported = caeData.exported;
    const importedCaeProject = new ImportedCaeProjectDTO();
    importedCaeProject.project = this.projectModel.currentProject;
    importedCaeProject.data = representationData as OBJRepresentationDataVO;
    return importedCaeProject;
  }

  public async saveProjects(importedProjects: ImportedCaeProjectDTO[], project: CaeProject) {
    try {
      this.projectModel.currentProject.projectGroup.groupName = project.name;
      for (const importedProject of importedProjects) {
        importedProject.project.projectGroup.groupName = project.name;
      }
      const projectGroup = await this.projectNavigationPresenter.openImportCaeDialog();
      await this.saveCaeProjectService.saveMultipleProjects(projectGroup, importedProjects);
    } catch (error) {
      Log.error('Could not open project dialog cae: ', error);
    }
  }
}
